import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { StrictMode } from "react";
import ReactDOM from "react-dom/client";
import { Toaster } from "sonner";
import "./index.css";

import { TooltipProvider } from "@radix-ui/react-tooltip";
import { ServiceProvider } from "./services";
import * as Sentry from "@sentry/react";
import {
  browserTracingIntegration,
  browserProfilingIntegration,
  replayIntegration,
} from "@sentry/react";
import {
  DeploymentInitialized,
  DeploymentProvider,
  useSession,
} from "@wacht/tanstack-router";

const isDevelopment = true;

declare global {
  interface Window {
    cioanalytics?: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      identify: (id: string, data: Record<string, any>) => void;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      group: (id: string, data: Record<string, any>) => void;
      reset: () => void;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      track: (event: string, data: Record<string, any>) => void;
      // add other methods if needed
    };
  }
}

Sentry.init({
  dsn: "https://e0cfa1aeda5b043b882300618614f143@o4506881307377664.ingest.us.sentry.io/4509161047392256",
  integrations: [
    browserTracingIntegration(),
    browserProfilingIntegration(),
    replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
    }),
  ],
  // Tracing
  tracesSampleRate: 1.0, // Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    "localhost",
    "http://localhost:8000",
    /^https:\/\/app\.inboxdoctor\.ai/,
  ],
  // Set profilesSampleRate to 1.0 to profile every transaction.
  profilesSampleRate: 1.0,
  // Session Replay
  replaysSessionSampleRate: isDevelopment ? 1.0 : 0.1, // 100% in development, 10% in production
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

const queryClient = new QueryClient();

// Import the generated route tree
import {
  CircleCheck,
  Info,
  Loader2,
  TriangleAlert,
  XCircle,
} from "lucide-react";
import { routeTree } from "./routeTree.gen";
import { CioAnalyticsScript } from "./components/scripts";
import ThemeWrapper from "./providers/theme-wrapper";

// Create a new router instance
const router = createRouter({
  routeTree,
  context: {
    auth: null,
  },
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (
  !PUBLISHABLE_KEY ||
  PUBLISHABLE_KEY === "pk_test_placeholder_replace_with_real_key"
) {
  console.warn(
    "Clerk Publishable Key not configured. Please add your Clerk Publishable Key to the .env file",
  );
  // For development, we'll continue without Clerk authentication
}

const WATCHER_KEY = import.meta.env.VITE_WATCHER_KEY;

if (!WATCHER_KEY) {
  console.warn(
    "Watcher Key not configured. Please add your Watcher Key to the .env file",
  );
}

// Render the app
const rootElement = document.getElementById("root")!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      {/*<ClerkProvider publishableKey={PUBLISHABLE_KEY} afterSignOutUrl="/signin">*/}
      <DeploymentProvider
        publicKey={WATCHER_KEY}
        uiOverwrites={{
          sign_in_page_url: "/signin",
          after_sign_out_all_page_url: "/signin",
          after_sign_out_one_page_url: "/signin",
        }}
      >
        <DeploymentInitialized>
          <ThemeWrapper>
            <ServiceProvider>
              <QueryClientProvider client={queryClient}>
                <TooltipProvider>
                  <InnerApp />
                </TooltipProvider>
              </QueryClientProvider>
            </ServiceProvider>
            <Toaster
              icons={{
                success: (
                  <CircleCheck className="text-green-500 size-4 shrink-0" />
                ),
                info: <Info className="text-blue-500 size-4 shrink-0" />,
                warning: (
                  <TriangleAlert className="text-yellow-500 size-4 shrink-0" />
                ),
                error: <XCircle className="text-red-500 size-4 shrink-0" />,
                loading: (
                  <Loader2 className="text-gray-500 animate-spin size-4 shrink-0" />
                ),
              }}
            />
          </ThemeWrapper>
          {/*</ClerkProvider>*/}
        </DeploymentInitialized>
      </DeploymentProvider>
      <CioAnalyticsScript />
    </StrictMode>,
  );
}

// eslint-disable-next-line react-refresh/only-export-components
function InnerApp() {
  const auth = useSession();
  return (
    <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
      <RouterProvider router={router} context={{ auth }} />
    </Sentry.ErrorBoundary>
  );
}
